import {
  SHStack,
  SHTableContainer,
  SHTypography,
} from "@components/design-systems";
import { useUserPermissions } from "@hooks/userUserPermission";
import { SelectedSubProductDTO } from "@models/managed-accounts/entities/step/fee";
import { AnalysisVirtualizedStepProps } from "@models/platform-analysis/entities/analysis";
import {
  FeatureAnalysisRowDTO,
  SelectedProductDTO,
} from "@models/platform-analysis/entities/steps/feature";
import { useWindowHeight } from "@react-hook/window-size";
import {
  ExpandedState,
  getCoreRowModel,
  getExpandedRowModel,
  Table,
  useReactTable,
} from "@tanstack/react-table";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useToggle } from "react-use";
import { TableVirtualizationConfigs as Configs } from "../../../configs";
import {
  getNewTableCollapseStateByLevel,
  getTableColumnDefs,
} from "../../../utils";
import { AnalysisTableToolBar } from "../../toolbar";
import { AnalysisTableBody } from "../body";
import { AnalysisTableHeader } from "../header";
import { AnalysisVirtualizedTableWrapper } from "../wrapper";

type Props = {
  stepNumber: number;
  disabled?: boolean;
  virtualizedProps: AnalysisVirtualizedStepProps;
  selectedProducts?: SelectedProductDTO[];
  selectedSubProducts?: SelectedSubProductDTO[];
  currentTableRows?: FeatureAnalysisRowDTO[];
  shouldAlignCenter?: boolean;
};
export const AnalysisTableContainer = ({
  stepNumber,
  virtualizedProps,
  selectedProducts,
  selectedSubProducts,
  currentTableRows = [],
  disabled = false,
  shouldAlignCenter = false,
}: Props) => {
  const { isSupplierGroup } = useUserPermissions();
  const windowHeight = useWindowHeight();
  const {
    matchParentContainerWidth,
    tableTitle,
    toolBarTitles,
    removeToolbar,
    allowFilters,
    ignoreScrollBackToTopEvent,
    showSectionTooltip,
    showSubSectionTooltip,
    getCurrentCollapseLevelFunc,
    getCollapseLevelChangedManuallyFunc,
    getHiddenColumnIndexesMapFunc,
    shouldHideCollapseArrow,
  } = virtualizedProps;

  const currentCollapseLevel = useSelector(getCurrentCollapseLevelFunc);
  const collapseStateChangedManually = useSelector(
    getCollapseLevelChangedManuallyFunc,
  );
  const hideCollapseArrow = useSelector(shouldHideCollapseArrow);
  const tableColumnVisibilityState = useSelector(getHiddenColumnIndexesMapFunc);

  const tableContainerRef = useRef<HTMLDivElement>(null);
  const collapseLockRef = useRef(false);

  const [isScrolledLeft, setIsScrolledLeft] = useToggle(true);
  const [shouldCollapse, toggleShouldCollapse] = useToggle(false);
  const [expandState, setExpandState] = useState<ExpandedState>({});

  const columns = useMemo(() => {
    return getTableColumnDefs({
      selectedProducts: selectedProducts,
      selectedSubProducts: selectedSubProducts,
      isSupplierGroup,
      title: tableTitle,
      hiddenColumnIndexesMap: tableColumnVisibilityState,
    });
  }, [
    selectedProducts,
    selectedSubProducts,
    isSupplierGroup,
    tableTitle,
    tableColumnVisibilityState,
  ]);

  const table: Table<FeatureAnalysisRowDTO> = useReactTable({
    data: currentTableRows,
    columns: columns,
    state: {
      expanded: expandState,
    },
    getCoreRowModel: getCoreRowModel(),
    getSubRows: (row) => row.subRows,
    onExpandedChange: setExpandState,
    getExpandedRowModel: getExpandedRowModel(),
    enableExpanding: true,
  });
  const tableHeaderGroups = table.getHeaderGroups();
  const tableRows = table.getRowModel().rows;
  const tableRowLength = tableRows.length;
  const tableColumns = table.getAllLeafColumns();

  const handleOnTableScroll = useCallback(
    (event: React.UIEvent<HTMLDivElement>) => {
      if (collapseLockRef.current) return;

      const { scrollLeft, scrollTop } = event.currentTarget || {};
      const isScrolledLeftNow = scrollLeft === 0;
      const isScrolledTopNow = scrollTop === 0;
      const shouldCollapseNow = isScrolledTopNow
        ? false
        : Configs.MinimumTableHeaderHeight +
            tableRowLength * Configs.DefaultRowHeight >
          windowHeight - Configs.DefaultStepperHeight;

      if (shouldCollapse !== shouldCollapseNow) {
        collapseLockRef.current = true;
        toggleShouldCollapse(shouldCollapseNow);
        setTimeout(() => {
          collapseLockRef.current = false;
          if (
            !ignoreScrollBackToTopEvent &&
            shouldCollapseNow &&
            tableContainerRef.current &&
            tableContainerRef.current.scrollTop <= Configs.DefaultRowHeight
          ) {
            tableContainerRef.current.scrollTop = 1;
          }
        }, 500);
      }

      if (isScrolledLeft !== isScrolledLeftNow) {
        setIsScrolledLeft(isScrolledLeftNow);
      }
    },
    [
      ignoreScrollBackToTopEvent,
      isScrolledLeft,
      shouldCollapse,
      tableRowLength,
      windowHeight,
      setIsScrolledLeft,
      toggleShouldCollapse,
    ],
  );

  // #region Effects & Variables
  useEffect(() => {
    if (collapseStateChangedManually) return;
    const newCollapseState = getNewTableCollapseStateByLevel({
      collapseLevel: currentCollapseLevel,
      currentData: currentTableRows,
    });
    setExpandState(newCollapseState);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTableRows, currentCollapseLevel, collapseStateChangedManually]);

  return (
    <>
      {!removeToolbar && (
        <AnalysisTableToolBar
          title={
            isSupplierGroup ? toolBarTitles?.default : toolBarTitles?.review
          }
          isDisabled={disabled}
          stepNumber={stepNumber ?? 0}
          hidden={shouldCollapse}
          allowFilters={allowFilters}
          virtualizedProps={virtualizedProps}
        />
      )}

      <SHTableContainer
        ref={tableContainerRef}
        sx={{
          overflow: "auto",
          width:
            shouldAlignCenter && !matchParentContainerWidth
              ? "fit-content"
              : "100%",
          maxWidth: "100%",
        }}
        onScroll={handleOnTableScroll}
      >
        <AnalysisVirtualizedTableWrapper
          scrolledLeft={isScrolledLeft}
          collapsed={shouldCollapse}
          hideArrow={hideCollapseArrow}
          showSectionTooltip={showSectionTooltip}
          showSubSectionTooltip={showSubSectionTooltip}
        >
          <AnalysisTableHeader tableHeaderGroups={tableHeaderGroups} />
          <AnalysisTableBody
            rows={tableRows}
            columns={tableColumns}
            containerRefElement={tableContainerRef.current}
          />
        </AnalysisVirtualizedTableWrapper>
      </SHTableContainer>

      {!tableRowLength && (
        <SHStack
          width={"100%"}
          alignItems="center"
          justifyContent={"center"}
          paddingTop={2}
        >
          <SHTypography variant="body4">
            No results match your criteria.
          </SHTypography>
        </SHStack>
      )}
    </>
  );
};
